import {
  Alert,
  Autocomplete,
  Box,
  Button,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import type { FC } from "react";
import { useCallback, useMemo } from "react";
import type { UseSupabaseLoaderState } from "../../../../../server/supabase/hooks/types";
import { useDialog } from "../../../../../utils/hooks/use-dialog";
import type { UUID } from "../../../../../utils/uuid";

import {
  SegmentCrudDataGridDialogV2Edit,
  SegmentCrudDataGridDialogV2New,
} from "../../../../segment/components/manager/segment-crud-data-grid-dialog-v2";
import type { SupabaseSegmentWithFavourite } from "../../../../segment/server/supabase-segment-service";

type SegmentInfo = {
  selected: UUID | null;
  createNew: UUID;
};

type Option = {
  label: string;
  value: UUID;
  description?: string;
  favorite: boolean;
  isNewAction?: boolean;
  isAdhoc?: boolean;
};

export const SegmentSelect: FC<{
  segmentLoader: UseSupabaseLoaderState<SupabaseSegmentWithFavourite[]>;
  onChange: (id: UUID | null) => void;
  segmentInfo?: SegmentInfo;
  allowNewSegment?: boolean;
  disabled?: boolean;
  loading?: boolean;
}> = ({
  segmentLoader: { loading: segmentLoaderLoading, error, data },
  onChange,
  segmentInfo,
  allowNewSegment = true,
  loading,
  disabled,
}) => {
  const {
    open: segmentModalOpen,
    handleOpen: openSegmentModal,
    handleClose: closeSegmentModal,
    data: segmentModalData,
  } = useDialog<{ mode: "add" } | { mode: "edit"; id: UUID }>();

  // 1. Memoize options and group them
  const options = useMemo(() => {
    if (!data) return [];

    const elems: Option[] = data.reduce((acc, segment) => {
      if (!segment.id || (!segment.name && segmentInfo?.selected !== segment.id)) return acc;

      acc.push({
        label: segment.name ?? "Ad Hoc Segment",
        value: segment.id,
        favorite: !!segment.favorite_segment,
        description: segment.description ?? "",
        isAdhoc: segment.name === null,
      });

      return acc;
    }, [] as Option[]);

    // Sort: Favorites first, then alphabetical
    elems.sort((a, b) => {
      if (a.favorite !== b.favorite) return a.favorite ? -1 : 1;
      return a.label.localeCompare(b.label);
    });

    if (allowNewSegment && segmentInfo?.createNew) {
      elems.push({
        label: "New Segment",
        value: segmentInfo.createNew,
        favorite: false,
        description: "",
        isNewAction: true,
      });
    }

    if (segmentInfo?.selected && !elems.some((e) => e.value === segmentInfo.selected)) {
      elems.push({
        label: "Ad Hoc Segment",
        value: segmentInfo.selected,
        favorite: false,
        description: "",
        isAdhoc: true,
      });
    }

    return elems;
  }, [data, segmentInfo, allowNewSegment]);

  // 2. Derived State: Find current selection object
  const selectedOption = useMemo(
    () => options.find((o) => o.value === segmentInfo?.selected) ?? null,
    [options, segmentInfo?.selected],
  );

  // 3. Derived State: Is the current selection an "Ad Hoc" segment?
  const isAdHocSelected = selectedOption?.isAdhoc ?? false;

  const handleChange = useCallback(
    (_: React.SyntheticEvent, value: Option | null) => {
      if (value?.isNewAction) {
        openSegmentModal({ mode: "add" });
        return;
      }
      onChange(value?.value ?? null);
    },
    [onChange, openSegmentModal],
  );

  if (error) {
    return <Alert severity="error">Error loading segments</Alert>;
  }

  if (segmentLoaderLoading && !data) return <LinearProgress />;

  return (
    <>
      <Stack spacing={1}>
        <Autocomplete
          value={selectedOption}
          options={options}
          loading={segmentLoaderLoading || loading}
          disabled={disabled || segmentLoaderLoading || loading}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          groupBy={(option) =>
            option.isNewAction ? "Actions" : option.favorite ? "⭐ Favorites" : "Existing Segments"
          }
          onChange={handleChange}
          renderInput={(params) => <TextField {...params} label="Segment" variant="standard" />}
          renderOption={(props, option) => {
            if (option.isNewAction) {
              return (
                <Box component="li" {...props} key={option.value}>
                  <Typography color="primary" fontWeight="bold">
                    Create new segment
                  </Typography>
                </Box>
              );
            }

            return (
              <Tooltip
                title={option.description}
                key={option.value}
                disableHoverListener={!option.description}
              >
                <Box component="li" {...props}>
                  <Typography>{option.label}</Typography>
                </Box>
              </Tooltip>
            );
          }}
        />

        {isAdHocSelected && segmentInfo?.selected && (
          <Button
            variant="text"
            size="small"
            onClick={() => openSegmentModal({ mode: "edit", id: segmentInfo.selected! })}
            data-analytics-id="edit_ad_hoc_segment"
          >
            Edit the ad hoc segment
          </Button>
        )}
      </Stack>

      {/* Modals handled via conditional rendering */}
      {segmentModalOpen && segmentModalData?.mode === "add" && (
        <SegmentCrudDataGridDialogV2New
          open={segmentModalOpen}
          is_recipient_type={false}
          onClose={closeSegmentModal}
          nameRequired={false}
          onSave={onChange}
        />
      )}
      {segmentModalOpen && segmentModalData?.mode === "edit" && (
        <SegmentCrudDataGridDialogV2Edit
          is_recipient_type={false}
          onClose={closeSegmentModal}
          segment_id={segmentModalData.id}
          disabled={false}
          nameRequired={false}
        />
      )}
    </>
  );
};
